import EmblaCarousel, {
  EmblaCarouselType,
  EmblaOptionsType,
} from 'embla-carousel';

type Target = string | HTMLElement;

export interface InitEmblaConfig {
  target: Target;
  options?: EmblaOptionsType;
  controls?: null | {
    prev?: string | null;
    next?: string | null;
  };
  dots?: null | {
    container?: string | HTMLElement | null;
    create?: boolean;
    dotClass?: string;
    selectedClass?: string;
    renderAriaLabel?: (index: number) => string;
  };
}

export interface InitEmblaResult {
  api: EmblaCarouselType;
  destroy: () => void;
  root: HTMLElement;
  prevEl?: HTMLElement | null;
  nextEl?: HTMLElement | null;
  dotsEl?: HTMLElement | null;
  dotNodes?: HTMLElement[];
}

export function initEmblaSlider(
  config: InitEmblaConfig
): InitEmblaResult | null {
  const { target, options } = config;

  const root =
    typeof target === 'string'
      ? (document.querySelector(target) as HTMLElement | null)
      : (target as HTMLElement | null);

  if (!root) {
    console.warn(
      `[initEmblaSlider] Root not found${
        typeof target === 'string' ? `: "${target}"` : ''
      }`
    );

    return null;
  }

  const api = EmblaCarousel(root, options ?? {});

  const controlsConfig =
    config.controls === null
      ? null
      : {
          prev: config.controls?.prev ?? '[data-embla-prev]',
          next: config.controls?.next ?? '[data-embla-next]',
        };

  const resolveInRoot = <T extends Element>(
    ref: string | HTMLElement | null | undefined
  ): T | null => {
    if (!ref) return null;
    if (typeof ref === 'string') return root.querySelector(ref) as T | null;
    return root.contains(ref) ? (ref as unknown as T) : null;
  };

  const prevEl =
    controlsConfig && controlsConfig.prev !== null
      ? resolveInRoot<HTMLElement>(controlsConfig.prev)
      : null;

  const nextEl =
    controlsConfig && controlsConfig.next !== null
      ? resolveInRoot<HTMLElement>(controlsConfig.next)
      : null;

  const hasControls = Boolean(controlsConfig && (prevEl || nextEl));

  const setDisabled = (
    el: HTMLElement | null | undefined,
    disabled: boolean
  ) => {
    if (!el) {
      return;
    }

    if (disabled) {
      el.setAttribute('disabled', 'disabled');
      el.setAttribute('aria-disabled', 'true');
    } else {
      el.removeAttribute('disabled');
      el.removeAttribute('aria-disabled');
    }
  };

  const toggleControls = () => {
    if (!hasControls) {
      return;
    }

    if (prevEl) {
      setDisabled(prevEl, !api.canScrollPrev());
    }

    if (nextEl) {
      setDisabled(nextEl, !api.canScrollNext());
    }
  };

  const onPrev = () => api.scrollPrev();
  const onNext = () => api.scrollNext();

  if (hasControls) {
    if (prevEl) {
      prevEl.addEventListener('click', onPrev, false);
    }

    if (nextEl) {
      nextEl.addEventListener('click', onNext, false);
    }

    api.on('select', toggleControls).on('reInit', toggleControls);

    toggleControls();
  }

  /* --------------------------- Dots ---------------------------- */
  const dotsConfig =
    config.dots === null
      ? null
      : {
          container: config.dots?.container ?? '[data-embla-dots]',
          create: config.dots?.create ?? true,
          dotClass: config.dots?.dotClass ?? 'embla__dot',
          selectedClass: config.dots?.selectedClass ?? 'embla__dot--selected',
          renderAriaLabel:
            config.dots?.renderAriaLabel ??
            ((i: number) => `Go to slide ${i + 1}`),
        };

  const resolveDotsContainer = (): HTMLElement | null => {
    if (!dotsConfig) {
      return null;
    }

    const ref = dotsConfig.container;

    if (!ref) {
      return null;
    }

    if (typeof ref === 'string') {
      return root.querySelector(ref);
    }

    return root.contains(ref) ? ref : null;
  };

  const dotsEl = dotsConfig ? resolveDotsContainer() : null;
  const hasDots = Boolean(dotsConfig && dotsEl);

  let dotNodes: HTMLElement[] = [];
  let dotsController: AbortController | null = null;

  const teardownDots = () => {
    if (!hasDots) {
      return;
    }

    if (dotsController) {
      dotsController.abort();
      dotsController = null;
    }

    if (dotsConfig!.create && dotsEl) {
      dotsEl.innerHTML = '';
    }

    dotNodes = [];
  };

  const updateDotsSelected = () => {
    if (!hasDots || !dotNodes.length) return;

    const selected = api.selectedScrollSnap();

    dotNodes.forEach((dot, i) => {
      const isSelected = i === selected;

      dot.classList.toggle(dotsConfig!.selectedClass, isSelected);

      if (isSelected) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  };

  const buildDots = () => {
    if (!hasDots || !dotsEl) {
      return;
    }

    teardownDots();

    const snapCount = api.scrollSnapList().length;

    if (dotsConfig!.create) {
      dotsEl.innerHTML = Array.from({ length: snapCount })
        .map(
          (_x, i) =>
            `<button class="${
              dotsConfig!.dotClass
            }" type="button" aria-label="${dotsConfig!.renderAriaLabel(
              i
            )}"></button>`
        )
        .join('');
    }

    const all = Array.from(
      dotsEl.querySelectorAll<HTMLElement>(`.${dotsConfig!.dotClass}`)
    );
    dotNodes = all.slice(0, snapCount);

    dotsController = new AbortController();

    dotNodes.forEach((dot, i) => {
      dot.addEventListener('click', () => api.scrollTo(i), {
        signal: dotsController!.signal,
      });
    });

    updateDotsSelected();
  };

  if (hasDots) {
    api.on('select', updateDotsSelected).on('reInit', buildDots);

    buildDots();
  }

  const destroy = () => {
    if (hasControls) {
      if (prevEl) {
        prevEl.removeEventListener('click', onPrev, false);
        prevEl.removeAttribute('disabled');
        prevEl.removeAttribute('aria-disabled');
      }
      if (nextEl) {
        nextEl.removeEventListener('click', onNext, false);
        nextEl.removeAttribute('disabled');
        nextEl.removeAttribute('aria-disabled');
      }
      try {
        (api as any).off?.('select', toggleControls);
        (api as any).off?.('reInit', toggleControls);
      } catch {}
    }

    if (hasDots) {
      teardownDots();
      try {
        (api as any).off?.('select', updateDotsSelected);
        (api as any).off?.('reInit', buildDots);
      } catch {}
    }

    api.destroy();
  };

  return { api, destroy, root, prevEl, nextEl, dotsEl, dotNodes };
}
